import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AdminContext';
import ItemList from '../../components/admin/ItemList';
import Swal from 'sweetalert2';
import {
  useLazyGetItemsQuery,
  useDeleteItemMutation
} from '../../api/businessApi';

const ItemManagement = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // RTK Query hooks
  const [getItems, { data: itemsData, isLoading: itemsLoading, error: itemsError }] = useLazyGetItemsQuery();
  const [deleteItem, { isLoading: deleteLoading }] = useDeleteItemMutation();

  // Check authentication
  useEffect(() => {
    if (!user) {
      navigate('/admin/login');
    }
  }, [user, navigate]);

  // Fetch items on component mount
  useEffect(() => {
    if (user) {
      getItems();
    }
  }, [getItems, user]);

  const handleCreateItem = () => {
    navigate('/admin/items/new');
  };

  const handleEdit = (item) => {
    navigate(`/admin/items/${item.id}`);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await deleteItem(id).unwrap();
        Swal.fire(
          'Deleted!',
          'Item has been deleted.',
          'success'
        );
        // Refresh items list
        getItems();
      } catch (error) {
        console.error('Error deleting item:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.data?.message || error.message || 'Failed to delete item',
        });
      }
    }
  };

  return (
    <div className="p-6">

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Item Management</h2>
        <button
          onClick={() => {
            handleCreateItem();
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
        >
          Add Item
        </button>
      </div>      {/* Error Message */}
      {itemsError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex">
            <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 0116 8 8 0 0116zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707a1 1 0 01-1.414 0l-7.071 7.071a1 1 0 01-1.414 1.414L10 11.414l7.071 7.071a1 1 0 01-1.414 1.414L10 12.586l-7.071 7.071a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
            <span className="text-red-700">{itemsError?.data?.message || "Failed to fetch items"}</span>
          </div>
        </div>
      )}

      {/* Items List */}
      {!itemsLoading && !itemsError && (
        <ItemList
          onEdit={handleEdit}
          onDelete={handleDelete}
          itemsData={itemsData}
          isLoading={itemsLoading}
          error={itemsError}
          onRefresh={(params) => getItems(params)}
        />
      )}
    </div>
  );
};

export default ItemManagement;
